import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { HomeStateData } from '@workspace/api-backend'
import {
  v2ApprovePortfolioContacts,
  v2PausePortfolioOutreach,
  v2ResumePortfolioOutreach,
} from '@workspace/api-backend'
import {
  v2GetPortfolioOutreachStatusOptions,
  v2GetPortfolioQueueOptions,
} from '@workspace/api-backend/react-query'
import { Dropzone } from '@workspace/ui/components/dropzone'
import { LoadingSpinner } from '@workspace/ui/components/loading-spinner'
import { useState } from 'react'
import { useShareLinksQuery } from '../../../hooks/api'
import { useCopyToClipboard } from '../../../hooks/use-copy-to-clipboard'
import { OnboardingLaneOverlay } from '../../onboarding-lane/components/onboarding-lane-overlay'
import { outreachApi, outreachQueueQueryKey, outreachStatusQueryKey } from '../../outreach/api'
import { OutreachTopUpDialog } from '../../outreach/components/outreach-topup-dialog'
import { useEnableOutreachMutation } from '../../outreach/hooks/use-enable-outreach-mutation'
import { useOutreachStatusQuery } from '../../outreach/hooks/use-outreach-status'
import type { BannerKey } from '../config/copy'
import { BANNER_CTA, HOME_CTA, outreachTabFor, PILL_TARGET } from '../config/cta-routes'
import { useDeckDrop } from '../hooks/use-deck-drop'
import { useHomeCtaRunner } from '../hooks/use-home-cta'
import {
  useHomeStateQuery,
  useInvalidateHomeState,
  useSetDistributionIntentMutation,
  useSnoozeShareStepMutation,
} from '../hooks/use-home-state'
import { HomeWizard } from './home-wizard'
import { ShareLinkVisual } from './visuals/share-link-visual'

/**
 * The campaign with the least runway left, as a one-element list so it slots
 * into the same field `drainedTracks` fills. Null runway means the track is off
 * or empty — nothing to approve more of.
 */
function lowestTrack(home?: HomeStateData): ('investor' | 'warm_intro')[] | undefined {
  const investor = home?.investorTrack.runwayDays ?? null
  const warm = home?.warmIntroTrack.runwayDays ?? null
  if (investor === null && warm === null) return undefined
  if (warm === null) return ['investor']
  if (investor === null) return ['warm_intro']
  return warm < investor ? ['warm_intro'] : ['investor']
}

/**
 * The founder's Home.
 *
 * One query resolves what to show; everything else here is wiring the card's
 * buttons to things that already exist. The dialogs are mounted at this level
 * rather than inside the card so the card stays a pure view.
 */
export function HomePage({ startupId }: { startupId: string }) {
  const homeQuery = useHomeStateQuery(startupId)
  const invalidateHome = useInvalidateHomeState(startupId)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Drives both the card's drop zone and its primary button, so "give us your
  // deck" has one implementation regardless of which one the founder uses.
  const deck = useDeckDrop(startupId, invalidateHome)

  const [topUpOpen, setTopUpOpen] = useState(false)
  // Shared with the visual's own inline copy button, so clicking either one
  // updates both — a founder must never see one button say "Copied" while an
  // identical one beside it still says "Copy".
  const { copied: justCopiedLink, copy: copyShareUrl } = useCopyToClipboard()

  // The connect dialog needs the outreach row for its stage logic. Same query
  // key the outreach page uses, so opening it here costs nothing extra.
  const outreachStatus = useOutreachStatusQuery(startupId)
  const enableOutreach = useEnableOutreachMutation(startupId)

  // The Share step's real link — same source the "Share your Profile" page
  // reads, so Home never invents a second copy of the URL.
  const shareLinksQuery = useShareLinksQuery(startupId)
  const shareLinks = shareLinksQuery.data?.data ?? []
  const defaultShareLink = shareLinks.find((l) => l.isDefault) ?? shareLinks[0]
  const shareUrl = defaultShareLink ? `${window.location.origin}/s/${defaultShareLink.token}` : null

  const snoozeShareStep = useSnoozeShareStepMutation(startupId)
  const setDistributionIntent = useSetDistributionIntentMutation(startupId)

  // Home speaks for the whole outreach effort, not one channel of it: a founder
  // pausing from here means "stop sending", and leaving the warm-intro campaign
  // running would be the opposite of what the button says. The portfolio call is
  // allowed to fail — a startup that never configured that channel has nothing
  // to pause, and that must not block the investor pause.
  const pauseOutreach = useMutation({
    mutationFn: async () => {
      await Promise.all([
        outreachApi.disable(startupId),
        v2PausePortfolioOutreach({ path: { id: startupId } }).catch(() => undefined),
      ])
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachStatusQueryKey(startupId) })
      await queryClient.invalidateQueries({
        queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
      })
      await invalidateHome()
    },
  })

  const approveEverything = async () => {
    await Promise.all([
      outreachApi.approveAllQueued(startupId).catch(() => undefined),
      v2ApprovePortfolioContacts({ path: { id: startupId } }).catch(() => undefined),
    ])
    await queryClient.invalidateQueries({ queryKey: outreachQueueQueryKey(startupId) })
    await queryClient.invalidateQueries({
      queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
    })
    await invalidateHome()
  }

  // Symmetrical, so a founder who paused everything from Home can undo it from
  // the same place.
  const resumeBoth = async () => {
    await Promise.all([
      enableOutreach.mutateAsync().catch(() => undefined),
      v2ResumePortfolioOutreach({ path: { id: startupId } }).catch(() => undefined),
    ])
    await queryClient.invalidateQueries({
      queryKey: v2GetPortfolioOutreachStatusOptions({ path: { id: startupId } }).queryKey,
    })
    await invalidateHome()
  }

  // What a campaign-board CTA needs to land on the right board: which channels
  // run, and which one the current card is about.
  //
  // Drained names its own track. Failing that, the campaign running lowest is
  // the one the card and the sidebar dot are about — "approve more" has to open
  // the board that is starving, not the healthy one beside it.
  const home = homeQuery.data?.data
  const campaign = {
    investorTrackEnabled: home?.investorTrack.enabled,
    tracks: home?.drainedTracks?.length ? home.drainedTracks : lowestTrack(home),
  }

  const runCta = useHomeCtaRunner(
    {
      onAddFunds: () => setTopUpOpen(true),
      onResumeSending: () => void resumeBoth(),
      onPauseSending: () => pauseOutreach.mutate(),
      // Approves both campaigns, because Home speaks for the whole outreach
      // effort — sending the founder to the match list instead left them to find
      // the two boards and repeat the action on each. Either call may fail
      // harmlessly on a startup that has only one campaign.
      onApproveAll: () => void approveEverything(),
      // Make sure a campaign exists, then switch to it. Provisioning has to
      // happen before there is anything to approve, and the outreach page is
      // where approving lives.
      //
      // Land on a campaign the founder is actually running. /outreach opens the
      // investor board by default, so a founder running warm intros only was sent
      // to an empty board with nothing to review — the one thing the card had
      // just asked them to do.
      onApproveContacts: async () => {
        if (!outreachStatus.data?.status) {
          await enableOutreach.mutateAsync().catch(() => undefined)
        }
        navigate({
          to: '/app/startup/$id/outreach',
          params: { id: startupId },
          search: { tab: outreachTabFor(campaign) },
        })
      },
      // Same trigger as the drop zone above it — the button used to navigate to
      // the profile page to find an upload control there.
      onUploadDeck: () => deck.open(),
      onCopyShareLink: () => copyShareUrl(shareUrl),
      // Flips intent back to SELF_SHARE and lands the founder on whatever the
      // resolver now shows — QUEST_SHARE, or past it if they already have an
      // organic view. Not a route: the point is state changes, not navigation.
      onEscapeToShare: () =>
        setDistributionIntent.mutate({ path: { startupId }, body: { intent: 'SELF_SHARE' } }),
    },
    campaign
  )

  if (homeQuery.isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-8rem)] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const data: HomeStateData | undefined = homeQuery.data?.data
  if (homeQuery.error || !data) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        We could not load your home page. Refreshing usually clears it.
      </div>
    )
  }

  const cta = HOME_CTA[data.key]

  return (
    <>
      <HomeWizard
        data={data}
        // The upload state gets the drop target in the card itself; every other
        // state uses its own visual.
        visualOverride={
          data.key === 'QUEST_UPLOAD_DECK' ? (
            <Dropzone
              title="Upload your pitch deck"
              hint="Click to browse, or drag a PDF here · max 50 MB"
              isDragActive={deck.isDragActive}
              isUploading={deck.isUploading}
              error={deck.error}
              rootProps={{ ...deck.getRootProps(), className: 'cursor-pointer' }}
            >
              <input {...deck.getInputProps()} />
            </Dropzone>
          ) : data.key === 'QUEST_SHARE' ? (
            <ShareLinkVisual
              url={shareUrl}
              copied={justCopiedLink}
              onCopy={() => copyShareUrl(shareUrl)}
            />
          ) : undefined
        }
        onPrimary={() => runCta(cta.primary)}
        onGhost={() => runCta(cta.ghost)}
        hasPrimary={cta.primary !== undefined}
        hasGhost={cta.ghost !== undefined}
        onBanner={(banner: BannerKey) => runCta(BANNER_CTA[banner])}
        onPillLink={() => runCta(PILL_TARGET)}
        primaryLabel={data.key === 'QUEST_SHARE' && justCopiedLink ? 'Copied' : undefined}
        onSnooze={() => snoozeShareStep.mutate({ path: { startupId } })}
      />

      <OutreachTopUpDialog startupId={startupId} open={topUpOpen} onOpenChange={setTopUpOpen} />

      {/* Shown once, the moment the profile first becomes shareable — gated on
          the stamp the mutation itself sets, so it is provably never shown
          twice. Past profile completion: the same set of keys the QUEST_SHARE
          gate sits after. */}
      {data.distributionIntentPromptedAt === null &&
        data.key !== 'QUEST_UPLOAD_DECK' &&
        data.key !== 'QUEST_READING_DECK' &&
        data.key !== 'QUEST_READ_FAILED' &&
        data.key !== 'QUEST_COMPLETE_PROFILE' && (
          <OnboardingLaneOverlay
            startupId={startupId}
            shareUrl={shareUrl}
            onDismiss={() => void invalidateHome()}
          />
        )}
    </>
  )
}
